import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb, { getCategoryName } from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  if (!article) {
    return <Navigate to="/" replace />;
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '小宝探花',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://npjyf.cn/article/${slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-14 px-4 md:px-6">
          <article className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: getCategoryName(article.category), href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>

            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万次观看` : `${article.views}次观看`}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>

            <div className="w-full aspect-video rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200">
              <h3 className="text-lg font-semibold font-heading text-foreground-900 mb-4">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all"
                  >
                    <div className="w-full aspect-video overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-xs md:text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1">{related.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </article>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}